import React, { createContext, useContext, useEffect, useState } from 'react'
import { Usuario, AuthContextType, UsuarioLogin, UsuarioCrear, RespuestaRegistro } from '../../types'
import { authService } from '../../services/authService'
import { esErrorApiAplicacion } from '../../services/apiError'
import { useToast } from '../../ganchos/interfaz-usuario/usarNotificaciones'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

function tituloErrorAutenticacion(code: string | null): string {
  if (code === 'validation_error') return 'Datos inválidos'
  if (code === 'unauthorized') return 'Credenciales incorrectas'
  if (code === 'forbidden') return 'Acceso restringido'
  return 'Error de autenticación'
}

function tituloErrorRegistro(code: string | null): string {
  if (code === 'validation_error') return 'Datos inválidos'
  if (code === 'conflict') return 'Registro duplicado'
  if (code === 'forbidden') return 'Registro no permitido'
  return 'Error en el registro'
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [usuario, setUsuario] = useState<Usuario | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { showToast } = useToast()

  // Verificar si hay un token guardado al cargar la aplicación
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const savedToken = localStorage.getItem('logixport_token')
        if (savedToken) {
          setToken(savedToken)
          const savedUser = authService.getUserFromStorage()
          if (savedUser) {
            setUsuario(savedUser)
          }
          // Verificar si el token es válido obteniendo la información del usuario
          const userData = await authService.getCurrentUser()
          setUsuario(userData)
          localStorage.setItem('logixport_user', JSON.stringify(userData))
        }
      } catch (error) {
        // Si el token no es válido, limpiarlo
        localStorage.removeItem('logixport_token')
        localStorage.removeItem('logixport_user')
        console.error('Error al verificar autenticación:', error)
      } finally {
        setIsLoading(false)
      }
    }

    initializeAuth()
  }, [])

  const login = async (credentials: UsuarioLogin) => {
    try {
      setIsLoading(true)
      const response = await authService.login(credentials)
      
      // Guardar token y datos del usuario
      setToken(response.access_token)
      localStorage.setItem('logixport_token', response.access_token)
      
      // Obtener información completa del usuario
      const userData = await authService.getCurrentUser()
      setUsuario(userData)
      localStorage.setItem('logixport_user', JSON.stringify(userData))
      
      showToast({
        type: 'success',
        title: '¡Bienvenido!',
        message: `Hola ${userData.nombre}, has iniciado sesión correctamente.`
      })
    } catch (error: unknown) {
      const code = esErrorApiAplicacion(error) ? error.code : null
      showToast({
        type: 'error',
        title: tituloErrorAutenticacion(code),
        message: error instanceof Error ? error.message : 'Credenciales incorrectas'
      })
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const loginGoogle = async (credential: string) => {
    try {
      setIsLoading(true)
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || undefined
      const response = await authService.loginGoogle(credential, clientId)
      
      // Guardar token y datos del usuario
      setToken(response.access_token)
      localStorage.setItem('logixport_token', response.access_token)
      
      // Obtener información completa del usuario
      const userData = await authService.getCurrentUser()
      setUsuario(userData)
      localStorage.setItem('logixport_user', JSON.stringify(userData))
      
      showToast({
        type: 'success',
        title: '¡Bienvenido!',
        message: `Hola ${userData.nombre}, has iniciado sesión con Google correctamente.`
      })
    } catch (error: unknown) {
      const code = esErrorApiAplicacion(error) ? error.code : null
      showToast({
        type: 'error',
        title: tituloErrorAutenticacion(code),
        message: error instanceof Error ? error.message : 'Error al iniciar sesión con Google'
      })
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const register = async (userData: UsuarioCrear): Promise<RespuestaRegistro> => {
    try {
      setIsLoading(true)
      const resultado = await authService.register(userData)
      
      try {
        await authService.sendWelcomeEmail(userData.email, userData.nombre)
      } catch (error) {
        console.warn('No se pudo enviar email de bienvenida:', error)
      }
      
      showToast({
        type: 'success',
        title: '¡Registro exitoso!',
        message: `Hola ${userData.nombre}, tu cuenta está lista con el plan Básico activo. Ahora puedes iniciar sesión.`
      })
      
      return resultado
    } catch (error: unknown) {
      const code = esErrorApiAplicacion(error) ? error.code : null
      showToast({
        type: 'error',
        title: tituloErrorRegistro(code),
        message: error instanceof Error ? error.message : 'No se pudo crear la cuenta'
      })
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const logout = () => {
    setUsuario(null)
    setToken(null)
    localStorage.removeItem('logixport_token')
    localStorage.removeItem('logixport_user')
    
    showToast({
      type: 'info',
      title: 'Sesión cerrada',
      message: 'Has cerrado sesión correctamente.'
    })
  }

  const refreshUser = async () => {
    try {
      if (token) {
        const userData = await authService.getCurrentUser()
        setUsuario(userData)
        localStorage.setItem('logixport_user', JSON.stringify(userData))
      }
    } catch (error) {
      console.error('Error al refrescar usuario:', error)
    }
  }

  const isAuthenticated = !!token && !!usuario

  const value: AuthContextType = {
    usuario,
    token,
    isLoading,
    isAuthenticated,
    login,
    loginGoogle,
    logout,
    register,
    refreshUser,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
